import { ShoppingAssistantMessage, ShoppingAssistantProduct, ShoppingAssistantOrder } from '../shoppingAssistant';
import { WardrobeItem, OutfitLayer, UserProfile } from '../types';
import { generateSocialMediaContent, findSimilarItems, getFashionAdvice, orchestrateAgent } from './geminiService';
import { CDN_URLS, GEMINI_CONFIG } from '../config';
import { ai } from '@shared/services/gemini-core';

export interface ShoppingAssistantContext {
    wardrobeItems: WardrobeItem[];
    currentOutfit?: OutfitLayer;
    userProfile?: UserProfile;
}

/**
 * Curates and ranks products based on visual compatibility and user style persona using Gemini.
 */
export const curateProductsWithAI = async (
    products: ShoppingAssistantProduct[],
    userContext: string,
    userProfile?: UserProfile
): Promise<ShoppingAssistantProduct[]> => {
    if (products.length <= 1) return products;

    try {
        const productList = products.map((p, i) => `${i}: ${p.title} by ${p.brand || 'Unknown'}`).join('\n');
        const prompt = `Based on the user's style persona (${userProfile?.stylePersona || 'Classic'}) and context: "${userContext}", 
rank these products by how well they match and complement their look. 
Return ONLY a comma-separated list of indices in order of relevance (e.g., "2,0,1").

Products:
${productList}`;

        const model = ai.getGenerativeModel({ model: GEMINI_CONFIG.FLASH_TEXT });
        const result = await model.generateContent(prompt);
        const rankingText = result.response.text();
        const ranking = rankingText.split(',').map((s: string) => parseInt(s.trim())).filter((n: number) => !isNaN(n));

        if (ranking.length > 0) {
            return ranking.map((idx: number) => products[idx]).filter(Boolean);
        }
    } catch (e) {
        console.warn("AI Curation failed, returning original list.", e);
    }
    return products;
};

// Mock Data
const MOCK_PRODUCTS: ShoppingAssistantProduct[] = [
    {
        id: 'p1',
        title: 'Floral Summer Dress',
        price: '$45.00',
        imageUrl: `${CDN_URLS.UNSPLASH}photo-1572804013309-59a88b7e92f1?auto=format&fit=crop&q=80&w=300`,
        productUrl: '#',
        rating: 4.5,
        reviewCount: 120,
        brand: 'Zara',
        availability: 'In Stock'
    },
    {
        id: 'p2',
        title: 'Classic White Tee',
        price: '$15.00',
        originalPrice: '$25.00',
        imageUrl: `${CDN_URLS.UNSPLASH}photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&q=80&w=300`,
        productUrl: '#',
        rating: 4.8,
        reviewCount: 350,
        brand: 'Uniqlo',
        availability: 'In Stock'
    },
    {
        id: 'p3',
        title: 'Denim Jacket',
        price: '$65.00',
        imageUrl: `${CDN_URLS.UNSPLASH}photo-1542272454315-4c01d7abdf4a?auto=format&fit=crop&q=80&w=300`,
        productUrl: '#',
        rating: 4.2,
        reviewCount: 85,
        brand: 'Levi\'s',
        availability: 'Low Stock'
    },
    {
        id: 'p4',
        title: 'Running Shoes',
        price: '$89.00',
        imageUrl: `${CDN_URLS.UNSPLASH}photo-1549298916-b41d501d3772?auto=format&fit=crop&q=80&w=300`,
        productUrl: '#',
        rating: 4.9,
        reviewCount: 500,
        brand: 'Nike',
        availability: 'In Stock'
    },
    {
        id: 'p5',
        title: 'Leather Handbag',
        price: '$120.00',
        imageUrl: `${CDN_URLS.UNSPLASH}photo-1584917865442-de89df76afd3?auto=format&fit=crop&q=80&w=300`,
        productUrl: '#',
        rating: 4.7,
        reviewCount: 45,
        brand: 'Coach',
        availability: 'In Stock'
    }
];

const MOCK_ORDERS: ShoppingAssistantOrder[] = [
    {
        orderId: 'ORD-12345',
        status: 'Shipped',
        date: '2023-10-25',
        total: '$135.00',
        items: [
            { product: MOCK_PRODUCTS[0], quantity: 1 },
            { product: MOCK_PRODUCTS[3], quantity: 1 }
        ],
        trackingUrl: '#',
        estimatedDelivery: '2025-10-28'
    },
    {
        orderId: 'ORD-67890',
        status: 'Processing',
        date: '2025-10-27',
        total: '$45.00',
        items: [
            { product: MOCK_PRODUCTS[0], quantity: 1 }
        ],
        estimatedDelivery: '2025-10-30'
    }
];

// Service Implementation
export const detectIntent = async (
    query: string,
    sessionId: string,
    context?: ShoppingAssistantContext
): Promise<ShoppingAssistantMessage> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const lowerQuery = query.toLowerCase();

    // 1. Product Search Intent
    if (lowerQuery.includes('dress') || lowerQuery.includes('shoe') || lowerQuery.includes('jacket') || lowerQuery.includes('bag') || lowerQuery.includes('buy') || lowerQuery.includes('shop') || lowerQuery.includes('search')) {
        // Filter mock products based on query (simple implementation)
        const products = MOCK_PRODUCTS.filter(p =>
            lowerQuery.includes(p.title.toLowerCase().split(' ')[2] || '') || // Match last word of title roughly
            lowerQuery.includes(p.brand?.toLowerCase() || '') ||
            true // Default to showing all if specific match fails for demo
        );

        // If query is specific, limit the "all" fallback
        const specificProducts = lowerQuery.includes('dress') ? MOCK_PRODUCTS.filter(p => p.title.toLowerCase().includes('dress')) :
            lowerQuery.includes('shoe') ? MOCK_PRODUCTS.filter(p => p.title.toLowerCase().includes('shoe')) :
                lowerQuery.includes('jacket') ? MOCK_PRODUCTS.filter(p => p.title.toLowerCase().includes('jacket')) :
                    lowerQuery.includes('bag') ? MOCK_PRODUCTS.filter(p => p.title.toLowerCase().includes('bag')) :
                        MOCK_PRODUCTS;

        return {
            id: `msg-${Date.now()}`,
            role: 'model',
            textKey: "shoppingAssistant_response_products",
            textParams: { category: lowerQuery.includes('dress') ? 'dresses' : 'items' },
            timestamp: Date.now(),
            payload: {
                type: 'products',
                data: specificProducts.length > 0 ? specificProducts : MOCK_PRODUCTS
            }
        };
    }

    // 2. Order Status Intent
    if (lowerQuery.includes('order') || lowerQuery.includes('status') || lowerQuery.includes('track')) {
        // Check for specific order ID in query
        const orderIdMatch = query.match(/ORD-\d+/i) || query.match(/12345/) || query.match(/67890/);

        let order;
        if (orderIdMatch) {
            order = MOCK_ORDERS.find(o => o.orderId.includes(orderIdMatch[0].toUpperCase()) || (orderIdMatch[0] === '12345' && o.orderId.includes('12345')) || (orderIdMatch[0] === '67890' && o.orderId.includes('67890')));
        } else {
            // Default to most recent
            order = MOCK_ORDERS[0];
        }

        if (order) {
            return {
                id: `msg-${Date.now()}`,
                role: 'model',
                textKey: "shoppingAssistant_response_order_status",
                textParams: { orderId: order.orderId },
                timestamp: Date.now(),
                payload: {
                    type: 'order_status',
                    data: order
                }
            };
        } else {
            return {
                id: `msg-${Date.now()}`,
                role: 'model',
                textKey: "shoppingAssistant_response_order_not_found",
                timestamp: Date.now()
            };
        }
    }

    // 3. Return Policy / FAQ
    if (lowerQuery.includes('return') || lowerQuery.includes('refund')) {
        return {
            id: `msg-${Date.now()}`,
            role: 'model',
            textKey: "shoppingAssistant_response_return_policy",
            timestamp: Date.now()
        };
    }

    // 4. Social Media Intent
    if (lowerQuery.includes('social') || lowerQuery.includes('post') || lowerQuery.includes('instagram') || lowerQuery.includes('caption')) {
        const outfit = context?.currentOutfit;
        if (outfit?.mainGarment) {
            try {
                const mainGarmentId = outfit.mainGarment.id;
                const appliedColor = outfit.appliedGarmentColors[mainGarmentId];
                const outfitDesc = `${outfit.mainGarment.nameKey} ${appliedColor ? `in ${appliedColor}` : ''}`;
                const displayImageUrl = outfit.poseOutputs[outfit.currentPoseInstruction] || outfit.mainGarment.url;

                const content = await generateSocialMediaContent(
                    outfitDesc,
                    displayImageUrl,
                    outfit.currentPoseInstruction
                );

                return {
                    id: `msg-${Date.now()}`,
                    role: 'model',
                    textKey: "shoppingAssistant_response_social_media",
                    timestamp: Date.now(),
                    payload: {
                        type: 'social_media',
                        data: {
                            instagram: content.instagram.text,
                            twitter: content.twitter.text,
                            linkedin: content.linkedin.text
                        }
                    }
                };
            } catch (error) {
                console.error("Failed to generate real social content, falling back to mock", error);
            }
        } else {
            return {
                id: `msg-${Date.now()}`,
                role: 'model',
                textKey: "shoppingAssistant_response_social_no_garment",
                timestamp: Date.now()
            };
        }
    }

    // 5. Booking Intent
    if (lowerQuery.includes('book') || lowerQuery.includes('appointment') || lowerQuery.includes('consultation')) {
        return {
            id: `msg-${Date.now()}`,
            role: 'model',
            textKey: "shoppingAssistant_response_booking",
            timestamp: Date.now(),
            payload: {
                type: 'booking',
                data: {
                    service: 'shoppingAssistant_service_styling_consultation',
                    availableSlots: ['Tomorrow at 10:00', 'Wednesday at 14:00', 'Friday at 09:00']
                }
            }
        };
    }

    // 6. Enhanced Product Search (Agentic Flow with Wardrobe Integration)
    if (lowerQuery.includes('dress') || lowerQuery.includes('shoe') || lowerQuery.includes('jacket') || lowerQuery.includes('bag') || lowerQuery.includes('buy') || lowerQuery.includes('shop') || lowerQuery.includes('search')) {
        let results: ShoppingAssistantProduct[] = [];

        // 6a. Search Wardrobe first (Shop your closet)
        const wardrobeMatches = context?.wardrobeItems?.filter(item =>
            lowerQuery.includes(item.category?.toLowerCase() || '') ||
            lowerQuery.includes(item.nameKey.toLowerCase()) ||
            lowerQuery.includes(item.id.toLowerCase())
        ).map(item => ({
            id: item.id,
            title: item.nameKey,
            price: 'ALREADY OWNED',
            imageUrl: item.url,
            productUrl: '#',
            isWardrobeItem: true,
            brand: item.brand || 'Your Wardrobe'
        })) || [];

        results.push(...wardrobeMatches);

        // 6b. Search Real Retail Catalog via Agent Orchestrator
        try {
            const agentResponse = await orchestrateAgent(
                'catalog-agent',
                query,
                {
                    userProfile: context?.userProfile,
                    currentOutfit: context?.currentOutfit
                }
            );

            if (agentResponse.suggestedItems) {
                const aiProducts = agentResponse.suggestedItems.map((item: any, idx: number) => ({
                    id: `ai-${idx}-${Date.now()}`,
                    title: item.name,
                    price: item.price,
                    imageUrl: item.imageUrl || `${CDN_URLS.UNSPLASH}photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&q=80&w=300`,
                    productUrl: item.productUrl,
                    brand: item.retailer,
                    description: item.matchReason,
                    isWardrobeItem: false
                }));

                // Apply AI Curation
                results = await curateProductsWithAI(
                    [...wardrobeMatches, ...aiProducts],
                    query,
                    context?.userProfile
                );
            }
        } catch (error) {
            console.error("Agentic search failed, falling back to mock results", error);
            // Fallback to mock search
            const catalogResults = MOCK_PRODUCTS.filter(p =>
                lowerQuery.includes(p.title.toLowerCase()) ||
                lowerQuery.includes(p.brand?.toLowerCase() || '')
            ).map(p => ({ ...p, isWardrobeItem: false }));
            results.push(...catalogResults);
        }

        if (results.length === 0) results = MOCK_PRODUCTS.slice(0, 3);

        return {
            id: `msg-${Date.now()}`,
            role: 'model',
            textKey: results.some(r => r.isWardrobeItem)
                ? "shoppingAssistant_response_wardrobe_matches"
                : "shoppingAssistant_response_retail_matches",
            timestamp: Date.now(),
            payload: {
                type: 'products',
                data: results
            }
        };
    }

    // 7. Greeting
    if (lowerQuery.includes('hi') || lowerQuery.includes('hello') || lowerQuery.includes('hey')) {
        return {
            id: `msg-${Date.now()}`,
            role: 'model',
            textKey: "shoppingAssistant_response_greeting",
            timestamp: Date.now()
        };
    }

    // Default: Professional Fashion Advice via Gemini Agent Orchestrator
    try {
        const agentResponse = await orchestrateAgent(
            'styling-agent',
            query,
            {
                dressingRoomContext: context?.currentOutfit?.mainGarment
                    ? `User is currently looking at a ${context.currentOutfit.mainGarment.nameKey} in the dressing room.`
                    : "User is in the dressing room.",
                userProfile: context?.userProfile
            }
        );

        return {
            id: `msg-${Date.now()}`,
            role: 'model',
            text: agentResponse.message || agentResponse.text || "Sorry, I couldn't generate advice.",
            timestamp: Date.now()
        };
    } catch (error) {
        return {
            id: `msg-${Date.now()}`,
            role: 'model',
            textKey: "shoppingAssistant_response_fallback",
            timestamp: Date.now()
        };
    }
};
